import { gql, useLazyQuery } from '@apollo/client';
import { FeatureGate } from '@orn/global/featureGates/featureGates.js';
import LoadingIndicator from '@orn/shared-web/components/LoadingIndicator';
import SlideoutMenu from '@orn/shared-web/components/SlideoutMenu';
import { CalendarIcon } from '@orn/shared-web/icons';
import * as React from 'react';

import { useUpdateWorkItem } from '../../../hooks/mutations/useUpdateWorkItem';
import { useFeatureGate } from '../../../hooks/useFeatureGates';
import { PATHS } from '../../../paths';
import { useNavigateWithDateRange } from '../../../providers/DateRangeProvider';
import { calloutToAlertRow } from '../../../utils/alertRow';
import { formatCalendarDate, toCurrency } from '../../../utils/formatting';
import { workItemToListedAlert } from '../../../utils/workItemAlertRow';
import AlertListItem from '../../AlertListItem';
import { WorkItemFragment } from '../../alerts/workItemFragment';
import AlertTypeIcon from '../../AlertTypeIcon';
import Entity from '../../entity';
import UpdateRecurringTransactionModal from '../../UpdateRecurringTransactionModal';
import { SlideoutAmountDisplay, SlideoutEntityHeader, SlideoutHeaderCaption } from '../header';
import {
  AccountRow,
  AttachmentRow,
  IsRecurringRow,
  JsonDebugRow,
  LastPaymentRow,
  NameRow,
  NotesRow,
  RecurringFrequencyRow,
  RecurringTypeRow,
  TransactionPairRow,
  YearlySpendRow,
} from '../rows';

import type {
  GetTransactionDetailsQuery,
  GetTransactionDetailsQueryVariables,
  TransactionSlideoutDetailsFragment,
} from './__generated__/TransactionSlideout.gql';
import type { ListedAlert } from '../../../utils/alertRow';

// Fragment with full transaction details including paired transactions
const TransactionSlideoutDetailsFragment = gql`
  fragment TransactionSlideoutDetails on Transaction {
    id
    amount
    date
    description
    rawDescription
    isPending
    rawJson
    pairedTransaction {
      id
      amount
      date
      description
      financialAccount {
        id
        name
        mask
        ...EntityAvatar
      }
    }
    merchant {
      id
      ...EntityAvatar
    }
    financialAccount {
      id
      name
      mask
      subtype
      ...EntityAvatar
    }
    recurringTransaction {
      id
      type
      description
      frequency
      nextOccurrence
      amountRange
      rawJson
      lastTransaction {
        id
        date
        amount
        financialAccount {
          id
          name
          mask
          ...EntityAvatar
        }
      }
    }
    bill {
      id
      source
      senderIdentifier
      createdAt
      description
      documents {
        ...AttachmentRowDocument
      }
    }
    workItem {
      ...WorkItemDetails
    }
  }
  ${Entity.Avatar.Fragment}
  ${AttachmentRow.Fragment}
  ${WorkItemFragment}
`;

const GET_TRANSACTION_DETAILS = gql`
  query GetTransactionDetails($id: ID!) {
    getTransaction(id: $id) {
      ...TransactionSlideoutDetails
    }
  }
  ${TransactionSlideoutDetailsFragment}
`;

export type Transaction = TransactionSlideoutDetailsFragment;
export type RecurringTransaction = TransactionSlideoutDetailsFragment['recurringTransaction'];
export type TransactionWorkItem = NonNullable<TransactionSlideoutDetailsFragment['workItem']>;

interface ITransactionAlertRowProps {
  /** The alert in the shared row form, its own transaction ref already dropped. */
  alert: ListedAlert;
  onDismiss?: () => void;
}

/**
 * The alert raised on the transaction being viewed, as the same row the alert
 * surfaces render. Its subtitle drops the amount and account the row would
 * normally carry, both of which the slideout already states above it.
 */
const TransactionAlertRow = ({ alert, onDismiss }: ITransactionAlertRowProps) => {
  const { title, subtitle, timestamp, status, tone } = calloutToAlertRow(alert);

  return (
    <AlertListItem
      icon={<AlertTypeIcon callout={alert} />}
      title={title}
      subtitle={subtitle}
      timestamp={timestamp}
      status={status}
      tone={tone}
      onDismiss={onDismiss}
    />
  );
};

interface IBaseTransactionSlideoutProps {
  /** The alert raised on this transaction, when there is one. */
  workItem?: TransactionWorkItem;
  isOpen?: boolean;
  /** Whether to render the `JsonDebugRow`s. Wired by the wrapper from `FeatureGate.DebugTransactionDetails`. */
  showDebugJson?: boolean;
  onClickFinancialAccount?: () => void;
  onClickMerchant?: () => void;
  /** Called when the user dismisses the work item. */
  onDismissWorkItem?: () => void;
  onEditRecurringName?: () => void;
  /** Called when user clicks a linked transaction (paired txn, last payment, etc.) */
  onNavigateToTransaction: (transactionId: string) => void;
  onOpenChange?: (isOpen: boolean) => void;
  recurringTransaction: RecurringTransaction;
  transaction: Transaction | null;
}

export const BaseTransactionSlideout = ({
  workItem,
  isOpen,
  showDebugJson = false,
  onClickFinancialAccount,
  onClickMerchant,
  onDismissWorkItem,
  onEditRecurringName,
  onNavigateToTransaction,
  onOpenChange,
  recurringTransaction,
  transaction,
}: IBaseTransactionSlideoutProps) => {
  return (
    <SlideoutMenu isOpen={isOpen} onOpenChange={onOpenChange} ariaLabel="Transaction details">
      {({ close }) => {
        if (!transaction) {
          return (
            <SlideoutMenu.Content>
              <div className="py-50 flex items-center justify-center">
                <LoadingIndicator size="lg" />
              </div>
            </SlideoutMenu.Content>
          );
        }

        const fullTransaction: Transaction = {
          ...transaction,
          recurringTransaction,
        };

        const amountNumber = Number(fullTransaction.amount);
        const isCredit = amountNumber < 0;
        const amount = toCurrency(Math.abs(amountNumber));
        const formattedAmount = isCredit ? `+${amount}` : amount;
        const isRecurring = !!recurringTransaction;
        const pairedTransaction = fullTransaction.pairedTransaction ?? null;
        const rawDescription = fullTransaction.rawDescription ?? null;
        const displayName = fullTransaction.merchant?.name ?? fullTransaction.description;
        const normalizedRawDescription = rawDescription?.trim().toLowerCase() ?? '';
        const normalizedDisplayName = displayName.trim().toLowerCase();

        return (
          <>
            <SlideoutMenu.Header onClose={close}>
              <div className="flex w-full flex-col items-center gap-4">
                <SlideoutEntityHeader
                  entity={fullTransaction.merchant ?? null}
                  name={displayName}
                  onClick={onClickMerchant}
                />
                <div className="flex w-full flex-col items-center justify-center">
                  <SlideoutAmountDisplay value={formattedAmount} />
                  <SlideoutHeaderCaption icon={<CalendarIcon className="size-4.5 text-utility-brand-300 shrink-0" />}>
                    {formatCalendarDate(fullTransaction.date, 'medium')}
                  </SlideoutHeaderCaption>
                </div>
              </div>
            </SlideoutMenu.Header>
            <SlideoutMenu.Content>
              {workItem && (
                <TransactionAlertRow
                  // The alert's own transaction reference is dropped — this
                  // row sits inside that transaction's slideout.
                  alert={{ ...workItemToListedAlert(workItem), transaction: null }}
                  onDismiss={onDismissWorkItem}
                />
              )}
              <div className="flex flex-col gap-3">
                {fullTransaction.bill && fullTransaction.bill.documents.length > 0 && (
                  <SlideoutMenu.Container className="overflow-y-auto">
                    <AttachmentRow bill={fullTransaction.bill} />
                  </SlideoutMenu.Container>
                )}
                <SlideoutMenu.Container className="overflow-y-auto">
                  {fullTransaction.financialAccount && (
                    <AccountRow financialAccount={fullTransaction.financialAccount} onClick={onClickFinancialAccount} />
                  )}
                  <TransactionPairRow
                    pairedTransaction={
                      pairedTransaction?.financialAccount
                        ? { ...pairedTransaction, financialAccount: pairedTransaction.financialAccount }
                        : null
                    }
                    onClick={onNavigateToTransaction}
                  />
                  {showDebugJson && <JsonDebugRow label="Transaction JSON" rawJson={fullTransaction.rawJson} />}
                </SlideoutMenu.Container>
                {isRecurring && (
                  <SlideoutMenu.Container className="overflow-y-auto">
                    <IsRecurringRow isRecurring={isRecurring} />
                    <NameRow name={recurringTransaction?.description} onEdit={onEditRecurringName} />
                    <RecurringTypeRow
                      type={recurringTransaction?.type}
                      amountRange={recurringTransaction?.amountRange}
                    />
                    <RecurringFrequencyRow frequency={recurringTransaction?.frequency} />
                    <LastPaymentRow
                      lastTransaction={
                        recurringTransaction?.lastTransaction?.financialAccount
                          ? {
                              ...recurringTransaction.lastTransaction,
                              financialAccount: recurringTransaction.lastTransaction.financialAccount,
                            }
                          : null
                      }
                      excludeTransactionId={fullTransaction.id}
                      onClick={onNavigateToTransaction}
                    />
                    <YearlySpendRow
                      amountRange={recurringTransaction?.amountRange}
                      frequency={recurringTransaction?.frequency}
                    />
                    {showDebugJson && (
                      <JsonDebugRow label="Recurring transaction JSON" rawJson={recurringTransaction?.rawJson} />
                    )}
                  </SlideoutMenu.Container>
                )}
                <NotesRow description={fullTransaction.bill?.description} />
              </div>
            </SlideoutMenu.Content>
            {rawDescription && normalizedRawDescription !== normalizedDisplayName && (
              <>
                <div className="grow" />
                <SlideoutMenu.Footer className="bg-secondary sticky bottom-0 pt-0">
                  <div className="text-tertiary text-center text-sm uppercase">{rawDescription}</div>
                </SlideoutMenu.Footer>
              </>
            )}
          </>
        );
      }}
    </SlideoutMenu>
  );
};

interface ITransactionSlideoutProps {
  /** The ID of the transaction to display */
  isOpen: boolean;
  onClose: () => void;
  transactionId: string | null;
}

const TransactionSlideout = ({ transactionId, isOpen, onClose }: ITransactionSlideoutProps) => {
  const navigate = useNavigateWithDateRange();
  const showDebugJson = useFeatureGate(FeatureGate.DebugTransactionDetails);
  const [isRenameModalOpen, setIsRenameModalOpen] = React.useState(false);

  const [fetchTransaction, { data, error }] = useLazyQuery<
    GetTransactionDetailsQuery,
    GetTransactionDetailsQueryVariables
  >(GET_TRANSACTION_DETAILS);

  const { archiveWorkItem } = useUpdateWorkItem();

  // Fetch transaction when slideout opens or the parent's transactionId changes.
  React.useEffect(() => {
    if (isOpen && transactionId) {
      void fetchTransaction({ variables: { id: transactionId } });
    }
  }, [transactionId, isOpen, fetchTransaction]);

  // Close slideout on error (errors are logged to Sentry via Apollo errorLink)
  React.useEffect(() => {
    if (error) {
      onClose();
    }
  }, [error, onClose]);

  const transaction = data?.getTransaction;

  const onClickMerchant = React.useCallback(() => {
    if (transaction?.merchant?.id) {
      onClose();
      void navigate(PATHS.MERCHANT(transaction.merchant.id));
    }
  }, [transaction?.merchant?.id, onClose, navigate]);

  const onClickFinancialAccount = React.useCallback(() => {
    if (transaction?.financialAccount?.id) {
      onClose();
      void navigate(PATHS.ACCOUNT(transaction.financialAccount.id));
    }
  }, [transaction?.financialAccount?.id, onClose, navigate]);

  const handleNavigateToTransaction = React.useCallback(
    (targetTransactionId: string) => {
      void fetchTransaction({ variables: { id: targetTransactionId } });
    },
    [fetchTransaction]
  );

  const handleDismissWorkItem = React.useCallback(async () => {
    if (transaction?.workItem?.id) {
      await archiveWorkItem(transaction.workItem.id);
    }
  }, [transaction?.workItem?.id, archiveWorkItem]);

  const handleSlideoutOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) {
        setIsRenameModalOpen(false);
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <BaseTransactionSlideout
        workItem={transaction?.workItem ?? undefined}
        isOpen={isOpen}
        showDebugJson={showDebugJson}
        onClickFinancialAccount={onClickFinancialAccount}
        onClickMerchant={transaction?.merchant?.id ? onClickMerchant : undefined}
        onDismissWorkItem={transaction?.workItem ? handleDismissWorkItem : undefined}
        onEditRecurringName={transaction?.recurringTransaction ? () => setIsRenameModalOpen(true) : undefined}
        onNavigateToTransaction={handleNavigateToTransaction}
        onOpenChange={handleSlideoutOpenChange}
        recurringTransaction={transaction?.recurringTransaction ?? null}
        transaction={transaction ?? null}
      />
      {transaction?.recurringTransaction && (
        <UpdateRecurringTransactionModal
          recurringTransaction={transaction.recurringTransaction}
          isOpen={isRenameModalOpen}
          onOpenChange={setIsRenameModalOpen}
        />
      )}
    </>
  );
};

export default TransactionSlideout;
